import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ProcedureRecord {
  id: string;
  cpt_code: string;
  name: string;
  specialty: string;
  billable_type: 'BILLABLE' | 'DISPLAY_ONLY';
  active: boolean;
}

export interface BundleItem {
  id: string;
  procedure_id: string;
  is_primary: boolean;
  expected_modifier: string | null;
  sort_order: number;
  default_selected: boolean;
  procedure: ProcedureRecord;
}

export interface ProcedureBundle {
  id: string;
  name: string;
  specialty: string;
  assistant_expected: boolean;
  diagnosis_keywords: string[];
  documentation_checklist: string[];
  notes: string | null;
  active: boolean;
  items: BundleItem[];
}

export function useProcedureBundles() {
  const [bundles, setBundles] = useState<ProcedureBundle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      // Load bundles
      const { data: bundleRows } = await supabase
        .from('procedure_bundles')
        .select('*')
        .eq('active', true)
        .order('specialty, name');

      if (!bundleRows) { setLoading(false); return; }

      // Load all items with procedures
      const { data: itemRows } = await supabase
        .from('procedure_bundle_items')
        .select('*, procedures(*)')
        .in('bundle_id', bundleRows.map(b => b.id))
        .order('sort_order');

      const result: ProcedureBundle[] = bundleRows.map(b => ({
        id: b.id,
        name: b.name,
        specialty: b.specialty,
        assistant_expected: b.assistant_expected,
        diagnosis_keywords: (b.diagnosis_keywords as string[]) || [],
        documentation_checklist: (b.documentation_checklist as string[]) || [],
        notes: b.notes,
        active: b.active,
        items: (itemRows || [])
          .filter(i => i.bundle_id === b.id)
          .map(i => ({
            id: i.id,
            procedure_id: i.procedure_id,
            is_primary: i.is_primary,
            expected_modifier: i.expected_modifier,
            sort_order: i.sort_order,
            default_selected: i.default_selected,
            procedure: i.procedures as unknown as ProcedureRecord,
          })),
      }));

      setBundles(result);
      setLoading(false);
    }
    load();
  }, []);

  return { bundles, loading };
}

export function useProcedureCatalog() {
  const [procedures, setProcedures] = useState<ProcedureRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('procedures')
        .select('*')
        .order('specialty, cpt_code');
      setProcedures((data as ProcedureRecord[]) || []);
      setLoading(false);
    }
    load();
  }, []);

  return { procedures, loading, setProcedures };
}
